import { entity, query, string, truthy, equals, TSQuery, TSRank, isNull, likeAny } from '~/prix'
import coalesce from '~/prix/functions/coalesce'
import concat from '~/prix/functions/concat'
import setWeight from '~/prix/functions/setWeight'
import toTsVector from '~/prix/functions/toTsVector'
import unaccent from '~/prix/functions/unaccent'

interface UserGeoState {
  stateAbbreviation?: string
}

function checkAndExtractCnpj(input: string): string | false {
  if (!isNaN(Number(input)) && String(input).length === 14) {
    return String(input)
  }

  const maskMatches = input.match(/(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})/g)
  if (maskMatches && maskMatches.length > 0) {
    return String(input.replace(/\D/g, ''))
  }

  return false
}

function checkAndExtractPartialCnpj(input: string): string | false {
  const digitsOnly = String(input).replace(/\D/g, '')
  if (digitsOnly.length > 0 && digitsOnly.length < 14) {
    return digitsOnly
  }
  return false
}

export default function legalEntitySearchOnly(inputSearch: string, userGeoState?: UserGeoState) {
  const legalEntityCnpj = checkAndExtractCnpj(inputSearch)
  const partialCnpj = checkAndExtractPartialCnpj(inputSearch)
  const inputWithTsWildcards = `${inputSearch.trim().replace(/\s/g, ':*&')}:*`

  const legalEntitySearchName = [
    toTsVector('unaccent', coalesce(entity('legalEntity').property('corporateName'), '')),
    toTsVector('unaccent', coalesce(entity('legalEntity').property('tradeName'), '')),
  ]

  const legalEntitySearchAddress = [
    toTsVector('unaccent', coalesce(entity('legalEntity').property('state'), '')),
    toTsVector('unaccent', coalesce(entity('legalEntity').property('city'), '')),
    toTsVector('unaccent', coalesce(entity('legalEntity').property('neighborhood'), '')),
    toTsVector('unaccent', coalesce(entity('legalEntity').property('publicPlace'), '')),
  ]

  if (legalEntityCnpj) {
    return query('legalEntity')
      .select({
        id: entity('legalEntity').property('id'),
        geo: string().value('legalEntity'),
        tradeName: entity('legalEntity').property('tradeName'),
        corporateName: entity('legalEntity').property('corporateName'),
        subtitle: concat([
          entity('legalEntity').property('cnpj'),
          string().value(' - '),
          entity('legalEntity').property('city'),
          string().value(' - '),
          entity('legalEntity').property('state'),
        ]),
        streetId: entity('legalEntityGeoprocessing').property('streetId'),
        searchRank: string().value('1'),
      })
      .join({
        current: entity('legalEntity').property('id'),
        target: entity('legalEntityGeoprocessing').property('legalEntityId'),
        join: 'inner',
      })
      .join({
        current: entity('legalEntityGeoprocessing').property('stateId'),
        target: entity('state').property('id'),
        join: 'left',
      })
      .join({
        current: entity('legalEntityGeoprocessing').property('cityId'),
        target: entity('city').property('id'),
        join: 'left',
      })
      .join({
        current: entity('legalEntityGeoprocessing').property('streetId'),
        target: entity('street').property('id'),
        join: 'left',
      })
      .where(
        ...[
          isNull(entity('legalEntity').property('deletedAt')),
          userGeoState && userGeoState.stateAbbreviation !== undefined
            ? equals(
                entity('legalEntity').property('state'),
                string().value(String(userGeoState.stateAbbreviation)),
              )
            : null,
          equals(entity('legalEntity').property('cnpj'), string().value(String(legalEntityCnpj))),
        ].filter(truthy),
      )
      .limit(20)
  }

  if (partialCnpj) {
    console.log("partial cnpj ", partialCnpj, " - userGeoState: ", userGeoState);
    
    return query('legalEntity')
      .select({
        id: entity('legalEntity').property('id'),
        geo: string().value('legalEntity'),
        tradeName: entity('legalEntity').property('tradeName'),
        corporateName: entity('legalEntity').property('corporateName'),
        subtitle: concat([
          entity('legalEntity').property('cnpj'),
          string().value(' - '),
          entity('legalEntity').property('city'),
          string().value(' - '),
          entity('legalEntity').property('state'),
        ]),
        streetId: string().value(''),
        searchRank: string().value('2'),
      })
      .where(
        ...[
          isNull(entity('legalEntity').property('deletedAt')),
          likeAny(
            entity('legalEntity').property('cnpj'),
            [string().value(`${String(partialCnpj)}%`)],
          ),
        ].filter(truthy),
      )
      .limit(20)
  }

  console.log("busca por texto: ", inputSearch, " - userGeoState: ", userGeoState);
  
  return query('legalEntity')
    .select({
      id: entity('legalEntity').property('id'),
      geo: string().value('legalEntity'),
      tradeName: entity('legalEntity').property('tradeName'),
      corporateName: entity('legalEntity').property('corporateName'),
      subtitle: concat([
        entity('legalEntity').property('cnpj'),
        string().value(' - '),
        entity('legalEntity').property('city'),
        string().value(' - '),
        entity('legalEntity').property('state'),
      ]),
      streetId: entity('legalEntityGeoprocessing').property('streetId'),
      searchRank: TSRank(
        [
          ...legalEntitySearchName.map(field => setWeight(field, 'C')),
          ...legalEntitySearchAddress.map(field => setWeight(field, 'D')),
        ],
        unaccent(string().value(inputWithTsWildcards)),
      ),
    })
    .join({
      current: entity('legalEntity').property('id'),
      target: entity('legalEntityGeoprocessing').property('legalEntityId'),
      join: 'inner',
    })
    .join({
      current: entity('legalEntityGeoprocessing').property('stateId'),
      target: entity('state').property('id'),
      join: 'left',
    })
    .join({
      current: entity('legalEntityGeoprocessing').property('cityId'),
      target: entity('city').property('id'),
      join: 'left',
    })
    .join({
      current: entity('legalEntityGeoprocessing').property('streetId'),
      target: entity('street').property('id'),
      join: 'left',
    })
    .where(
      ...[
        isNull(entity('legalEntity').property('deletedAt')),
        userGeoState && userGeoState.stateAbbreviation !== undefined
          ? equals(
              entity('legalEntity').property('state'),
              string().value(String(userGeoState.stateAbbreviation)),
            )
          : null,
        TSQuery(
          [...legalEntitySearchName, ...legalEntitySearchAddress],
          unaccent(string().value(inputWithTsWildcards)),
        ),
      ].filter(truthy),
    )
    .limit(50)
}
